import { useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function NewChatModal({ open, onClose, providers, onCreateChat }) {
  const [title, setTitle] = useState("New Chat");
  const [providerId, setProviderId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!providerId) return;
    setLoading(true);
    try {
      await onCreateChat({ provider_id: providerId, title: title || "New Chat" });
      setTitle("New Chat");
      setProviderId("");
      onClose(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="new-chat-modal"
        className="bg-[#111620] border border-[#273142] shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-0 rounded-2xl max-w-md [&>button]:hidden"
      >
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold tracking-tight">
              NEW CHAT
            </DialogTitle>
            <button
              data-testid="close-new-chat-modal"
              onClick={() => onClose(false)}
              className="text-[#475569] hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-6 pt-4 space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">
              Chat Title
            </Label>
            <Input
              data-testid="new-chat-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Research Discussion"
              className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00] text-sm"
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.1em] text-[#94A3B8] mb-2 block">
              Provider
            </Label>
            {providers.length === 0 ? (
              <div className="border border-[#273142] border-dashed p-3 text-center">
                <p className="text-[#475569] text-xs font-mono">No providers available</p>
                <p className="text-[#475569] text-[10px] mt-1">Add a provider first</p>
              </div>
            ) : (
              <Select value={providerId} onValueChange={setProviderId}>
                <SelectTrigger data-testid="chat-provider-select" className="bg-[#080B10] border-[#273142] text-white rounded-lg focus:border-[#E5FF00] focus:ring-1 focus:ring-[#E5FF00]">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent className="bg-[#111620] border-[#273142] rounded-lg">
                  {providers.map((p) => (
                    <SelectItem
                      key={p.id}
                      value={p.id}
                      className="text-white hover:bg-[#1C2433] focus:bg-[#1C2433] rounded-md"
                    >
                      <span>{p.display_name}</span>
                      <span className="font-mono text-[10px] text-[#475569] ml-2">
                        {p.provider} / {p.model_name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              data-testid="create-chat-button"
              disabled={loading || !providerId}
              className="bg-[#E5FF00] text-[#080B10] font-semibold uppercase tracking-wider text-xs px-4 py-2 hover:bg-[#D4EC00] transition-colors rounded-lg disabled:opacity-50"
            >
              {loading ? "CREATING..." : "CREATE CHAT"}
            </button>
            <button
              type="button"
              onClick={() => onClose(false)}
              className="bg-transparent text-white border border-[#273142] hover:border-[#E5FF00] hover:text-[#E5FF00] text-xs px-4 py-2 transition-colors rounded-lg"
            >
              CANCEL
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
